import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit,
  Delete,
  Refresh,
} from '@mui/icons-material';
import {
  getPackagingMaterials,
  createPackagingMaterial,
  updatePackagingMaterial,
  deletePackagingMaterial,
} from '../../services/packagingMaterial';

const PackagingMaterialsPage = () => {
  const { t } = useTranslation();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    material_type: 'carton',
    size_category: 'small',
    length_cm: '',
    width_cm: '',
    height_cm: '',
    price_per_unit: '',
    is_active: true,
    description: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const response = await getPackagingMaterials();
      setMaterials(Array.isArray(response) ? response : response.results || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (material = null) => {
    if (material) {
      setSelectedMaterial(material);
      setFormData({
        name: material.name || '',
        material_type: material.material_type || 'carton',
        size_category: material.size_category || 'small',
        length_cm: material.length_cm || '',
        width_cm: material.width_cm || '',
        height_cm: material.height_cm || '',
        price_per_unit: material.price_per_unit || '',
        is_active: material.is_active !== undefined ? material.is_active : true,
        description: material.description || '',
      });
    } else {
      setSelectedMaterial(null);
      setFormData({
        name: '',
        material_type: 'carton',
        size_category: 'small',
        length_cm: '',
        width_cm: '',
        height_cm: '',
        price_per_unit: '',
        is_active: true,
        description: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMaterial(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        length_cm: formData.length_cm ? parseFloat(formData.length_cm) : null,
        width_cm: formData.width_cm ? parseFloat(formData.width_cm) : null,
        height_cm: formData.height_cm ? parseFloat(formData.height_cm) : null,
        price_per_unit: parseFloat(formData.price_per_unit),
      };

      if (selectedMaterial) {
        await updatePackagingMaterial(selectedMaterial.id, data);
        setSnackbar({ open: true, message: 'Packaging material updated successfully', severity: 'success' });
      } else {
        await createPackagingMaterial(data);
        setSnackbar({ open: true, message: 'Packaging material created successfully', severity: 'success' });
      }
      handleCloseDialog();
      loadMaterials();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await deletePackagingMaterial(selectedMaterial.id);
      setSnackbar({ open: true, message: 'Packaging material deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedMaterial(null);
      loadMaterials();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      flex: 1,
    },
    {
      field: 'material_type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={({ carton: 'Carton', sack_bag: 'Sack Bag', nylon: 'Nylon', foam: 'Foam', tape: 'Tape', bubble_wrap: 'Bubble Wrap' })[params.value] || params.value}
          size="small"
          color={params.value === 'carton' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'size_category',
      headerName: 'Size',
      width: 120,
      renderCell: (params) => {
        const sizeLabels = {
          xsmall: 'XSmall',
          small: 'Small',
          medium: 'Medium',
          large: 'Large',
          xlarge: 'XLarge',
        };
        return <Typography variant="body2">{sizeLabels[params.value] || params.value}</Typography>;
      },
    },
    {
      field: 'dimensions_display',
      headerName: 'Dimensions',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 'N/A'}</Typography>
      ),
    },
    {
      field: 'price_per_unit',
      headerName: 'Price',
      width: 120,
      renderCell: (params) => {
        const currency = '₦';
        return (
          <Typography variant="body2" fontWeight="bold">
            {currency}{parseFloat(params.value).toFixed(2)}
          </Typography>
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
          >
            <Edit />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedMaterial(params.row);
              setDeleteDialogOpen(true);
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#DC143C" sx={{ mb: 2 }}>
          Packaging Materials
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadMaterials}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B01030' } }}
          >
            Add Material
          </Button>
        </Box>
      </Box>

      {/* DataGrid */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={materials}
              columns={columns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem'
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: 'white'
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedMaterial ? 'Edit Packaging Material' : 'Create Packaging Material'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Material Type</InputLabel>
                <Select
                  value={formData.material_type}
                  label="Material Type"
                  onChange={(e) => setFormData({ ...formData, material_type: e.target.value })}
                >
                  <MenuItem value="carton">Carton</MenuItem>
                  <MenuItem value="sack_bag">Sack Bag</MenuItem>
                  <MenuItem value="nylon">Nylon</MenuItem>
                  <MenuItem value="foam">Foam</MenuItem>
                  <MenuItem value="tape">Tape</MenuItem>
                  <MenuItem value="bubble_wrap">Bubble Wrap</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Size Category</InputLabel>
                <Select
                  value={formData.size_category}
                  label="Size Category"
                  onChange={(e) => setFormData({ ...formData, size_category: e.target.value })}
                >
                  <MenuItem value="xsmall">Extra Small</MenuItem>
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                  <MenuItem value="xlarge">Extra Large</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.material_type === 'carton' && (
              <>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Length (cm)"
                    type="number"
                    value={formData.length_cm}
                    onChange={(e) => setFormData({ ...formData, length_cm: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Width (cm)"
                    type="number"
                    value={formData.width_cm}
                    onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Height (cm)"
                    type="number"
                    value={formData.height_cm}
                    onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Price per Unit (₦)"
                type="number"
                value={formData.price_per_unit}
                onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })}
                required
                inputProps={{ step: '0.01', min: '0' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: 'text.secondary' }}>
                        ₦
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.name || !formData.price_per_unit}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B01030' } }}
          >
            {selectedMaterial ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Packaging Material</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedMaterial?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PackagingMaterialsPage;
